import { Add, CheckCircle, Close, Edit, Error } from "@mui/icons-material";
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Icon,
    IconButton,
    Modal,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import IntercomIcon from "../../static/intercom-icon.svg";
import ChikoIcon from "../../static/chiko-icon.webp";
import { globalStore } from "../../store/global.atoms";
import ZendeskIntegration from "./components/ZendeskIntegration";

const IntegrationsPage = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

    const tenantIdAtom = useAtomValue(globalStore.userDataAtom)?.tenantId;

    const [open, setOpen] = useState(false);
    const [apiKey, setApiKey] = useState("");
    const [apiSecret, setApiSecret] = useState("");
    const [webhookUrl, setWebhookUrl] = useState("");
    const [validationMessage, setValidationMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [intercomIntegrationStatus, setIntercomIntegrationStatus] =
        useState("waiting");
    const [intercomIntegrationData, setIntercomIntegrationData] = useState({});
    const [intercomEditModalOpen, setIntercomEditModalOpen] = useState(false);

    const [websiteModalOpen, setWebsiteModalOpen] = useState(false);
    const [tagCopied, setTagCopied] = useState(false);

    const [chikoInboxIntegrationStatus, setChikoInboxIntegrationStatus] =
        useState("waiting");
    const [chikoInboxModalOpen, setChikoInboxModalOpen] = useState(false);
    const [chikoInboxIntegrationData, setChikoInboxIntegrationData] = useState(
        {},
    );

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const fetchIntegration = useCallback(async () => {
        try {
            const response = await fetch(
                `${backendEndpoint}/integration/whatsapp/`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": getCookieValue("csrfToken"),
                    },
                },
            );

            if (!response.ok) {
                setWebhookUrl("");
                return;
            }

            const json = await response.json();
            setApiKey(json.apiKey || "");
            setApiSecret(json.apiSecret || "");
            setWebhookUrl(`${backendEndpoint}${json.webhookUrl}` || "");
        } catch (error) {
            setWebhookUrl("");
        }
    }, [backendEndpoint, getCookieValue]);

    const handleValidateApi = async () => {
        let validationMessage = null;

        setIsLoading(true);
        setValidationMessage(validationMessage);

        try {
            const response = await fetch(
                `${backendEndpoint}/integration/whatsapp/validate/`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": getCookieValue("csrfToken"),
                    },
                    body: JSON.stringify({ apiKey: apiKey }),
                },
            );

            const json = await response.json();
            if (response.ok && json.valid) {
                validationMessage = {
                    type: "success",
                    text: "API ativada com sucesso.",
                };
                setValidationMessage(validationMessage);
            } else {
                validationMessage = {
                    type: "error",
                    text: "API informada é inválida.",
                };
                setValidationMessage(validationMessage);
            }
        } catch (error) {
            validationMessage = { type: "error", text: "Erro ao validar API." };
            setValidationMessage(validationMessage);
        } finally {
            setIsLoading(false);
        }

        return validationMessage;
    };

    const handleSubmit = async () => {
        const validationMessage = await handleValidateApi();
        if (validationMessage?.type !== "success") return;

        setIsLoading(true);
        const response = await fetch(
            `${backendEndpoint}/integration/whatsapp/`,
            {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: JSON.stringify({
                    apiKey: apiKey,
                    apiSecret: apiSecret,
                }),
            },
        );

        if (response.ok) {
            const json = await response.json();
            setWebhookUrl(`${backendEndpoint}${json.webhookUrl}` || "");
        }

        setIsLoading(false);
    };

    const handleStartIntercomIntegration = async () => {
        setIntercomIntegrationStatus("waiting");

        const response = await fetch(`${backendEndpoint}/oauth2/intercom/`, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const json = await response.json();
        const intercomUrl = json.url;

        const integrationWindow = window.open(
            intercomUrl,
            "Intercom Integration",
            "width=1050,height=600",
        );
        checkIntercomIntegrationWindow(integrationWindow);
    };

    const checkIntercomIntegrationWindow = (integrationWindow) => {
        if (integrationWindow.closed) {
            window.dispatchEvent(new Event("intercomIntegrationDone"));
        } else {
            window.setTimeout(
                () => checkIntercomIntegrationWindow(integrationWindow),
                500,
            );
        }
    };

    const updateIntercomIntegrationStatus = useCallback(async () => {
        setIntercomIntegrationStatus("waiting");

        const response = await fetch(
            `${backendEndpoint}/integration/intercom/`,
            {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
            },
        );

        const json = await response.json();

        if (!json.email) {
            setIntercomIntegrationStatus("add");
            return;
        }

        setIntercomIntegrationData(json);
        setIntercomIntegrationStatus("edit");
    }, [backendEndpoint, getCookieValue]);

    const handleEditIntercomIntegration = async () => {
        await fetch(`${backendEndpoint}/integration/intercom/`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
            body: JSON.stringify({
                online: intercomIntegrationData.online,
                reassign: intercomIntegrationData.reassign,
            }),
        });

        setIntercomEditModalOpen(false);
        updateIntercomIntegrationStatus();
    };

    const handleDeleteIntercomIntegration = async () => {
        await fetch(`${backendEndpoint}/integration/intercom/`, {
            method: "DELETE",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        setIntercomEditModalOpen(false);
        updateIntercomIntegrationStatus();
    };

    const fetchChikoInboxIntegration = useCallback(async () => {
        setChikoInboxIntegrationStatus("waiting");

        const response = await fetch(
            `${backendEndpoint}/integration/chiko-inbox/`,
            {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
            },
        );

        const json = await response.json();

        if (!json.active) {
            setChikoInboxIntegrationStatus("add");
            return;
        }

        setChikoInboxIntegrationData(json);
        setChikoInboxIntegrationStatus("edit");
    }, [backendEndpoint, getCookieValue]);

    const handleUpdateChikoInboxIntegration = useCallback(async () => {
        const response = await fetch(
            `${backendEndpoint}/integration/chiko-inbox/`,
            {
                method: "PUT",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: JSON.stringify(chikoInboxIntegrationData),
            },
        );

        if (response.ok) {
            setChikoInboxModalOpen(false);
        }
    }, [backendEndpoint, getCookieValue, chikoInboxIntegrationData]);

    useEffect(() => {
        fetchIntegration();
        updateIntercomIntegrationStatus();
        fetchChikoInboxIntegration();
    }, [
        fetchIntegration,
        updateIntercomIntegrationStatus,
        fetchChikoInboxIntegration,
    ]);

    useEffect(() => {
        const listenIntercomIntegration = () =>
            window.setTimeout(() => updateIntercomIntegrationStatus(), 500);
        window.addEventListener(
            "intercomIntegrationDone",
            listenIntercomIntegration,
        );
        return () =>
            window.removeEventListener(
                "intercomIntegrationDone",
                listenIntercomIntegration,
            );
    }, [updateIntercomIntegrationStatus]);

    return (
        <Box sx={{ width: "100%" }} id="integrations-container">
            <Box
                variant="h4"
                sx={{
                    width: "150",
                    color: "#333",
                    fontSize: "24px",
                    fontWeight: "500",
                    mb: 6,
                    padding: "10px 18px",
                }}
            >
                Integrações
                <Box
                    variant="div"
                    sx={{
                        fontSize: "13px",
                        fontWeight: 400,
                        pr: "10px",
                    }}
                >
                    Conecte o Chiko aos seus principais canais de conversa com
                    seus clientes
                </Box>
            </Box>

            <Box
                sx={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}
            >
                <Box
                    id="chiko-inbox-integration-box"
                    sx={{
                        height: 200,
                        width: 250,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Icon
                            sx={{
                                fontSize: 24,
                                textAlign: "center",
                                position: "relative",
                            }}
                        >
                            <img
                                src={ChikoIcon}
                                alt="Chiko Inbox"
                                style={{
                                    width: "100%",
                                    position: "absolute",
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                }}
                            />
                        </Icon>
                        <Typography sx={{ fontSize: 24, ml: 1 }} variant="p">
                            Chiko Inbox
                        </Typography>
                    </Box>
                    <Button
                        sx={{ mt: 4 }}
                        variant="contained"
                        startIcon={
                            chikoInboxIntegrationStatus === "add" ? (
                                <Add />
                            ) : chikoInboxIntegrationStatus === "waiting" ? (
                                <CircularProgress size="15px" color="inherit" />
                            ) : (
                                <Edit />
                            )
                        }
                        onClick={
                            chikoInboxIntegrationStatus === "add"
                                ? () =>
                                      window.open(
                                          "https://wa.me/+5521996367947?text=Ol%C3%A1,%20quero%20ativar%20o%20Chiko%20Inbox%20na%20minha%20conta!",
                                          "_blank",
                                      )
                                : chikoInboxIntegrationStatus === "edit"
                                  ? () => setChikoInboxModalOpen(true)
                                  : null
                        }
                    >
                        {chikoInboxIntegrationStatus === "add"
                            ? "Adicionar"
                            : chikoInboxIntegrationStatus === "waiting"
                              ? "Verificando..."
                              : "Editar"}
                    </Button>
                    <Typography sx={{ position: "absolute", bottom: 12 }}>
                        <Link
                            to="https://www.chiko.ai/chiko-inbox"
                            target="_blank"
                            style={{
                                width: "100%",
                                color: "inherit",
                                fontSize: 14,
                            }}
                        >
                            Saiba mais
                        </Link>
                    </Typography>
                </Box>
                {/* <Box
                    id="website-integration-box"
                    sx={{
                        height: 200,
                        width: 250,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Language sx={{ fontSize: 24 }} />
                        <Typography sx={{ fontSize: 24, ml: 1 }} variant="p">
                            Site Web
                        </Typography>
                    </Box>
                    <Button
                        sx={{ mt: 4 }}
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setWebsiteModalOpen(true)}
                    >
                        Adicionar
                    </Button>
                </Box>
                <Box
                    id="whatsapp-integration-box"
                    sx={{
                        height: 200,
                        width: 250,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <WhatsApp sx={{ fontSize: 24 }} />
                        <Typography sx={{ fontSize: 24, ml: 1 }} variant="p">
                            WhatsApp
                        </Typography>
                    </Box>
                    <Button
                        sx={{ mt: 4 }}
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setOpen(true)}
                    >
                        Adicionar
                    </Button>
                </Box> */}
                <Box
                    id="intercom-integration-box"
                    sx={{
                        height: 200,
                        width: 250,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Icon
                            sx={{
                                fontSize: 24,
                                textAlign: "center",
                                position: "relative",
                            }}
                        >
                            <img
                                src={IntercomIcon}
                                alt="Intercom"
                                style={{
                                    width: "100%",
                                    position: "absolute",
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                }}
                            />
                        </Icon>
                        <Typography sx={{ fontSize: 24, ml: 1 }} variant="p">
                            Intercom
                        </Typography>
                    </Box>
                    <Button
                        sx={{ mt: 4 }}
                        variant="contained"
                        startIcon={
                            intercomIntegrationStatus === "add" ? (
                                <Add />
                            ) : intercomIntegrationStatus === "waiting" ? (
                                <CircularProgress size="15px" color="inherit" />
                            ) : (
                                <Edit />
                            )
                        }
                        onClick={
                            intercomIntegrationStatus === "add"
                                ? () => handleStartIntercomIntegration()
                                : intercomIntegrationStatus === "waiting"
                                  ? null
                                  : () => setIntercomEditModalOpen(true)
                        }
                    >
                        {intercomIntegrationStatus === "add"
                            ? "Adicionar"
                            : intercomIntegrationStatus === "waiting"
                              ? "Verificando..."
                              : "Editar"}
                    </Button>
                </Box>
                <ZendeskIntegration />
            </Box>

            <Modal
                open={websiteModalOpen}
                // onClose={() => setWebsiteModalOpen(false)}
            >
                <Box
                    sx={{
                        width: "400px",
                        bgcolor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        mx: "auto",
                        mt: "10%",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={() => setWebsiteModalOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h6" gutterBottom>
                        Integração com Site Web
                    </Typography>

                    <TextField
                        fullWidth
                        label="Tag do Chiko"
                        variant="outlined"
                        multiline
                        rows={11}
                        value={`<!-- Chiko Widget -->\n<script>\n_chiko_tenant_id = "${tenantIdAtom}";\nwindow._chiko_tenant_id = _chiko_tenant_id;\n</script>\n<script src="${window.location.origin}/widget.js"></script>\n<!-- End Chiko Widget -->`}
                        onClick={(e) => {
                            if (!e.target.select || !e.target.value) return;

                            e.target.select();
                            document.execCommand("copy", true, e.target.value);
                            e.target.blur();
                            setTagCopied(true);
                            setTimeout(() => setTagCopied(false), 3000);
                        }}
                        sx={{ mt: 2 }}
                    />

                    {tagCopied && (
                        <Box
                            sx={{
                                mt: 1,
                                display: "flex",
                                alignItems: "center",
                                color: "green",
                            }}
                        >
                            <CheckCircle sx={{ mr: 1 }} />
                            <Typography variant="body2">
                                Tag copiada com sucesso!
                            </Typography>
                        </Box>
                    )}

                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Instruções:
                        <br />
                        1. Clique no texto para copiar a tag do Chiko
                        <br />
                        2. Cole a tag do Chiko no cabeçalho do seu site
                    </Typography>

                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => setWebsiteModalOpen(false)}
                        disabled={isLoading}
                        sx={{ mt: 2 }}
                    >
                        Fechar
                    </Button>
                </Box>
            </Modal>

            <Modal
                open={open}
                // onClose={() => setOpen(false)}
            >
                <Box
                    sx={{
                        width: "400px",
                        bgcolor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        mx: "auto",
                        mt: "10%",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={() => setOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h6" gutterBottom>
                        Integração com WhatsApp
                    </Typography>

                    <TextField
                        fullWidth
                        label="Chave de API"
                        variant="outlined"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        sx={{ mt: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Token de Verificação"
                        variant="outlined"
                        value={apiSecret}
                        onChange={(e) => setApiSecret(e.target.value)}
                        sx={{ mt: 2 }}
                    />

                    {validationMessage && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                color:
                                    validationMessage.type === "success"
                                        ? "green"
                                        : "red",
                            }}
                        >
                            {validationMessage.type === "success" ? (
                                <CheckCircle sx={{ mr: 1 }} />
                            ) : (
                                <Error sx={{ mr: 1 }} />
                            )}
                            <Typography variant="body2">
                                {validationMessage.text}
                            </Typography>
                        </Box>
                    )}

                    {webhookUrl && (
                        <>
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                Instruções:
                                <br />
                                1. Adicione o webhook:{" "}
                                <strong>{webhookUrl}</strong>
                                <br />
                                2. Dê a permissão de mensagens
                            </Typography>
                        </>
                    )}

                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        sx={{ mt: 2 }}
                    >
                        {isLoading ? "Processando..." : "Salvar"}
                    </Button>
                </Box>
            </Modal>

            <Modal
                open={intercomEditModalOpen}
                // onClose={() => setIntercomEditModalOpen(false)}
            >
                <Box
                    sx={{
                        width: "400px",
                        bgcolor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        mx: "auto",
                        mt: "10%",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={() => setIntercomEditModalOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h6" gutterBottom>
                        Integração com Intercom
                    </Typography>

                    <TextField
                        fullWidth
                        label="Nome"
                        variant="outlined"
                        value={intercomIntegrationData.name}
                        sx={{ mt: 2 }}
                        disabled
                        slotProps={{
                            htmlInput: {
                                readOnly: true,
                                disableUnderline: true,
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Email"
                        variant="outlined"
                        value={intercomIntegrationData.email}
                        sx={{ mt: 2 }}
                        disabled
                        slotProps={{
                            htmlInput: {
                                readOnly: true,
                                disableUnderline: true,
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Worspace"
                        variant="outlined"
                        value={intercomIntegrationData.workspaceName}
                        sx={{ mt: 2 }}
                        disabled
                        slotProps={{
                            htmlInput: {
                                readOnly: true,
                                disableUnderline: true,
                            },
                        }}
                    />

                    <FormGroup>
                        <FormControlLabel
                            label="Online"
                            sx={{ mt: 2 }}
                            control={
                                <Switch
                                    checked={intercomIntegrationData.online}
                                    onChange={(e) =>
                                        setIntercomIntegrationData({
                                            ...intercomIntegrationData,
                                            online: e.target.checked,
                                            reassign: !e.target.checked
                                                ? intercomIntegrationData.reassign
                                                : false,
                                        })
                                    }
                                />
                            }
                        />

                        {!intercomIntegrationData.online && (
                            <FormControlLabel
                                label="Reatribuir"
                                sx={{ mt: 2 }}
                                control={
                                    <Switch
                                        checked={
                                            intercomIntegrationData.reassign
                                        }
                                        onChange={(e) =>
                                            setIntercomIntegrationData({
                                                ...intercomIntegrationData,
                                                reassign:
                                                    !intercomIntegrationData.online
                                                        ? e.target.checked
                                                        : false,
                                            })
                                        }
                                    />
                                }
                            />
                        )}
                    </FormGroup>

                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => handleEditIntercomIntegration()}
                        sx={{ mt: 2 }}
                    >
                        Salvar
                    </Button>

                    <Button
                        fullWidth
                        variant="contained"
                        color="error"
                        onClick={() => handleDeleteIntercomIntegration()}
                        sx={{ mt: 2 }}
                    >
                        Deletar
                    </Button>
                </Box>
            </Modal>

            <Modal
                open={chikoInboxModalOpen}
                // onClose={() => setChikoInboxModalOpen(false)}
            >
                <Box
                    sx={{
                        width: "400px",
                        bgcolor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        mx: "auto",
                        mt: "10%",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={() => setChikoInboxModalOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h6" gutterBottom>
                        Integração com Chiko Inbox
                    </Typography>

                    <FormGroup>
                        <FormControlLabel
                            label="Aviso de início/fim de conversa"
                            sx={{ mt: 2 }}
                            control={
                                <Switch
                                    checked={
                                        chikoInboxIntegrationData.conversationOpenCloseWarning
                                    }
                                    onChange={(e) =>
                                        setChikoInboxIntegrationData({
                                            ...chikoInboxIntegrationData,
                                            conversationOpenCloseWarning:
                                                e.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                    </FormGroup>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => handleUpdateChikoInboxIntegration()}
                        sx={{ mt: 2 }}
                    >
                        Salvar
                    </Button>
                </Box>
            </Modal>
        </Box>
    );
};

export default IntegrationsPage;
